import { useEffect, useRef } from 'react';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { useDatalayerService } from './useDatalayerService';

interface UseIdentityDatalayerParams {
    request: IRequestContext;
    customer: Customer | undefined;
    telemetry: ITelemetry;
    isSignInPage?: boolean;
}

export const useIdentityDatalayer = ({ request, telemetry, customer, isSignInPage = false }: UseIdentityDatalayerParams): void => {
    const datalayerService = useDatalayerService({ request, telemetry });
    const hasFiredRef = useRef(false);

    useEffect(() => {
        if (typeof window === 'undefined' || isSignInPage) {
            return;
        }
        datalayerService.startInitialIdentityMonitor();

        if (hasFiredRef.current) {
            return;
        }
        if (request.user.isAuthenticated && !customer) {
            return;
        }

        hasFiredRef.current = true;

        const pathname = window.location.pathname;
        const isOrderConfirmationPage = pathname.includes('orderconfirmation');
        if (isOrderConfirmationPage) {
            return;
        }

        const isCheckoutPage = pathname.includes('checkout');
        if (isCheckoutPage && !request.user.isAuthenticated) {
            const stored = datalayerService.readGuestCheckoutIdentity();
            if (stored?.email || stored?.shippingAddress || stored?.billingAddress) {
                datalayerService.pushCheckoutIdentity(stored.email, stored.shippingAddress, stored.billingAddress);
                datalayerService.pushSignUpIfUserIsNew();
                return;
            }
        }

        datalayerService.pushIdentity(customer);
        datalayerService.pushSignUpIfUserIsNew();
    }, [customer, datalayerService, request.user.isAuthenticated, isSignInPage]);
};
